import { useCallback, useEffect, useRef, useState } from "react";
import { apiPost } from "../lib/apiClient.js";
import { beginIntent } from "../lib/telemetry/instrument";
import { R_START } from "../lib/timeline/geometry.js";
import { prefersReducedMotion } from "../lib/util/reducedMotion.js";

const DAY_MS = 86400000;
const FLY_MS = 1100;

const dayOf = (iso) => {
  const t = iso ? new Date(`${String(iso).slice(0, 10)}T12:00:00`).getTime() : NaN;
  return Number.isFinite(t) ? (t - R_START) / DAY_MS : null;
};
const ease = (k) => (k < 0.5 ? 4 * k * k * k : 1 - Math.pow(-2 * k + 2, 3) / 2);

/**
 * The Listen briefing (docs/report-listen.md): fetches it for the report being shown and
 * lends the player the real timeline for its "watch" scene — the rail panel tucked away, the
 * view flown to each trigger as the voice reaches it — putting everything back on close.
 *
 * @param {object} deps
 * @param {(rd: string, animate?: boolean, startDate?: string|null, buffer?: number|null, keepToday?: boolean) => void} deps.autoZoom
 * @param {{current: number}} deps.vlRef
 * @param {{current: number}} deps.ppdRef
 * @param {{current: {w: number}}} deps.cSizeRef
 * @param {{current: number|null}} deps.animRef the timeline's shared animation frame
 * @param {() => void} deps.scheduleRender
 * @param {boolean} deps.railPanelOpen
 * @param {() => void} deps.toggleRail
 */
export function useReportListen({
  autoZoom,
  vlRef,
  ppdRef,
  cSizeRef,
  animRef,
  scheduleRender,
  railPanelOpen,
  toggleRail,
}) {
  const [listen, setListen] = useState(null); // {status, briefing, error, outline, anchors, triggerEvents}
  const savedRef = useRef(null);
  const abortRef = useRef(null);
  const listenRef = useRef(null);
  // autoZoom is rebuilt on most renders; reading it through a ref keeps timelineCue stable.
  const autoZoomRef = useRef(autoZoom);
  useEffect(() => {
    listenRef.current = listen;
    autoZoomRef.current = autoZoom;
  }, [listen, autoZoom]);

  const start = useCallback(
    async ({ rowKey, outline, anchors, triggerEvents, forecastId = null }) => {
      abortRef.current?.abort();
      const ac = new AbortController();
      abortRef.current = ac;
      savedRef.current = { vl: vlRef.current, ppd: ppdRef.current, rail: railPanelOpen };
      // The briefing plays over the whole screen; the rail panel would only crowd the timeline.
      if (railPanelOpen) toggleRail();
      setListen({
        status: "loading",
        briefing: null,
        error: null,
        outline,
        anchors,
        triggerEvents,
      });
      const intent = beginIntent(
        "listen.start",
        { forecast_id: forecastId },
        { origin: "report.listen", surface: "report" },
      );
      const res = await apiPost(
        "/api/report/listen",
        { rowKey, outline },
        { signal: ac.signal, intentId: intent.id || null },
      );
      if (ac.signal.aborted) {
        intent.end("cancelled");
        return;
      }
      const made = res.ok && Array.isArray(res.body?.scenes) && res.body.scenes.length > 0;
      intent.end(made ? "ok" : "error");
      if (!made) {
        setListen(
          (l) =>
            l && {
              ...l,
              status: "error",
              error: res.body?.message || res.body?.error || "The briefing could not be made.",
            },
        );
        return;
      }
      setListen((l) => l && { ...l, status: "ready", briefing: res.body });
    },
    [vlRef, ppdRef, railPanelOpen, toggleRail],
  );

  /** Pan and zoom so `day` sits mid-screen with `span` days across, over FLY_MS. */
  const fly = useCallback(
    (day, span) => {
      const w = cSizeRef.current?.w || 800;
      const fromPpd = ppdRef.current;
      const fromCenter = vlRef.current + w / (2 * fromPpd);
      const toPpd = w / span;
      if (animRef.current) cancelAnimationFrame(animRef.current);
      if (prefersReducedMotion()) {
        animRef.current = null;
        ppdRef.current = toPpd;
        vlRef.current = day - w / (2 * toPpd);
        scheduleRender();
        return;
      }
      const t0 = performance.now();
      const step = (now) => {
        const k = ease(Math.min(1, (now - t0) / FLY_MS));
        // Zoom geometrically so the move reads as one smooth push in or pull out.
        const ppd = fromPpd * Math.pow(toPpd / fromPpd, k);
        ppdRef.current = ppd;
        vlRef.current = fromCenter + (day - fromCenter) * k - w / (2 * ppd);
        scheduleRender();
        animRef.current = k < 1 ? requestAnimationFrame(step) : null;
      };
      animRef.current = requestAnimationFrame(step);
    },
    [cSizeRef, ppdRef, vlRef, animRef, scheduleRender],
  );

  /** Fly to a trigger (`T1`…), or with no id pull back to the whole path to resolution. */
  const timelineCue = useCallback(
    (ref) => {
      const l = listenRef.current;
      if (!l) return;
      const resolution = l.outline?.resolution?.date || null;
      const ev = ref ? l.triggerEvents?.[ref] : null;
      const target =
        ev && typeof ev.atMs === "number" ? (ev.atMs - R_START) / DAY_MS : dayOf(ev?.date);
      if (target == null) {
        const last = Object.values(l.triggerEvents || {})
          .map((t) => t.date)
          .filter(Boolean)
          .sort()
          .pop();
        if (resolution || last) {
          autoZoomRef.current(resolution || last, !prefersReducedMotion(), null, null, true);
        }
        return;
      }
      const today = (Date.now() - R_START) / DAY_MS;
      const end = dayOf(resolution) ?? target + 60;
      // Close enough to read the trigger's neighbourhood, wide enough to keep its context.
      const span = Math.min(540, Math.max(21, (Math.max(end, target) - today) * 0.45));
      fly(target, span);
    },
    [fly],
  );

  const close = useCallback(() => {
    abortRef.current?.abort();
    const saved = savedRef.current;
    if (saved) {
      if (animRef.current) cancelAnimationFrame(animRef.current);
      animRef.current = null;
      vlRef.current = saved.vl;
      ppdRef.current = saved.ppd;
      scheduleRender();
      if (saved.rail && !railPanelOpen) toggleRail();
    }
    savedRef.current = null;
    setListen(null);
  }, [vlRef, ppdRef, animRef, scheduleRender, railPanelOpen, toggleRail]);

  return { listen, start, close, timelineCue };
}
